import { CONSTANTS } from "../../data";
import type { GameState } from "../../types";

export const progressionReducer = (state: GameState, action: any): GameState => {
  switch (action.type) {
    case "ADD_COINS":
      return { ...state, coins: state.coins + action.amount };

    case "SPEND_COINS":
      return { ...state, coins: Math.max(0, state.coins - action.amount) };

    case "ADD_XP":
      return { ...state, xp: state.xp + action.amount };

    case "LEVEL_UP":
      return {
        ...state,
        level: action.newLevel,
        levelUpNotification: action.newLevel,
      };

    case "UNLOCK_LEGENDARY": {
      const newUnlocks = { ...state.legendaryUnlocks };
      newUnlocks[action.unlockType] = true;
      return { ...state, legendaryUnlocks: newUnlocks };
    }

    case "UPDATE_QUEST_PROGRESS":
      return {
        ...state,
        quests: state.quests.map(quest =>
          quest.id === action.questId
            ? { ...quest, progress: Math.min(quest.target, quest.progress + action.amount) }
            : quest
        ),
      };

    case "COMPLETE_QUEST":
      return {
        ...state,
        quests: state.quests.filter(q => q.id !== action.questId),
      };

    case "ADD_QUEST":
      return {
        ...state,
        quests: [...state.quests, action.quest],
      };

    case "START_CHALLENGE":
      return {
        ...state,
        masterChefChallenge: {
          active: true,
          id: action.challengeId,
          startTime: action.challenge.startTime,
          progress: 0,
          goal: action.challenge.goal,
          duration: action.challenge.duration,
          trackingKey: action.challenge.trackingKey
        }
      };

    case "UPDATE_CHALLENGE_PROGRESS":
      if (!state.masterChefChallenge.active) return state;
      
      {
        const challenge = CONSTANTS.MASTER_CHEF_CHALLENGES[state.masterChefChallenge.id];
        if (!challenge || challenge.trackingKey !== action.trackingKey) return state;
        
        return {
          ...state,
          masterChefChallenge: {
            ...state.masterChefChallenge,
            progress: Math.min(challenge.goal, state.masterChefChallenge.progress + action.amount)
          }
        };
      }

    case "COMPLETE_CHALLENGE":
      return {
        ...state,
        masterChefChallenge: {
          active: false,
          id: null,
          startTime: 0,
          progress: 0,
          goal: 0,
          duration: 0
        }
      };

    case "UPGRADE_AI_BARISTA": {
      const currentLevel = state.aiBarista.level;
      const nextLevel = currentLevel + 1;
      const isUnlocking = currentLevel === 0;
      const unlockCost = CONSTANTS.AI_BARISTA.UNLOCK_COST;
      const unlockLevelReq = CONSTANTS.AI_BARISTA.UNLOCK_LEVEL;
      const upgradeData = CONSTANTS.AI_BARISTA.LEVELS[nextLevel];
    
      if (isUnlocking) {
        if (state.level >= unlockLevelReq && state.coins >= unlockCost) {
          return {
            ...state,
            coins: state.coins - unlockCost,
            aiBarista: {
              ...state.aiBarista,
              level: 1,
              speed: CONSTANTS.AI_BARISTA.LEVELS[1].speed,
              efficiency: CONSTANTS.AI_BARISTA.LEVELS[1].efficiency,
            },
          };
        }
      } else if (upgradeData && state.coins >= upgradeData.cost) {
        return {
          ...state,
          coins: state.coins - upgradeData.cost,
          aiBarista: {
            ...state.aiBarista,
            level: nextLevel,
            speed: upgradeData.speed,
            efficiency: upgradeData.efficiency,
          },
        };
      }
      return state;
    }

    case "TOGGLE_AI_BARISTA":
      if (state.aiBarista.level > 0) {
        return {
          ...state,
          aiBarista: {
            ...state.aiBarista,
            active: !state.aiBarista.active,
          },
        };
      }
      return state;

    case "UPGRADE_EQUIPMENT": {
      const { equipmentId } = action;
      const currentLevel = state.equipmentLevels[equipmentId];
      const upgradeInfo = CONSTANTS.EQUIPMENT_UPGRADES[equipmentId]?.levels[currentLevel + 1];

      if (upgradeInfo && state.coins >= upgradeInfo.cost && state.level >= upgradeInfo.unlockLevel) {
        return {
          ...state,
          coins: state.coins - upgradeInfo.cost,
          equipmentLevels: {
            ...state.equipmentLevels,
            [equipmentId]: currentLevel + 1,
          },
        };
      }
      return state;
    }

    case "PURCHASE_DECORATION": {
      const { decorationId } = action;
      const decoMap = (CONSTANTS as any).DECORATION_SHOP_ITEMS || {};
      const deco = decoMap[decorationId];

      if (deco && state.coins >= deco.price && (!deco.unlockLevel || state.level >= deco.unlockLevel)) {
        const newDecorations = { ...(state.decorations || {}), [decorationId]: true };
        return {
          ...state,
          coins: state.coins - deco.price,
          decorations: newDecorations,
        };
      }
      return state;
    }

    default:
      return state;
  }
};
